const CLIENT_ID = "624531684919-shqq114ibilc3uen1bmvt3v8seldcj9o.apps.googleusercontent.com";
const DISCOVERY_DOC = "https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest";
const SCOPES = "https://www.googleapis.com/auth/calendar.readonly";

let tokenClient = null;
let gapiInited = false;
let gisInited = false;
let pendingCallback = null;
let pendingErrorCallback = null;

function saveToken(token) {
  const expiry = Date.now() + (token.expires_in - 60) * 1000;
  localStorage.setItem("gapi-token", JSON.stringify({ ...token, expiry }));
}

function loadToken() {
  try {
    const raw = localStorage.getItem("gapi-token");
    if (!raw) return null;
    const token = JSON.parse(raw);
    if (Date.now() > token.expiry) {
      localStorage.removeItem("gapi-token");
      return null;
    }
    return token;
  } catch { return null; }
}

function checkReady(onReady) {
  if (gapiInited && gisInited) onReady();
}

export function initGoogleCalendar(onReady) {
  const gapiScript = document.createElement("script");
  gapiScript.src = "https://apis.google.com/js/api.js";
  gapiScript.onload = () => {
    window.gapi.load("client", async () => {
      await window.gapi.client.init({ discoveryDocs: [DISCOVERY_DOC] });
      const saved = loadToken();
      if (saved) window.gapi.client.setToken(saved);
      gapiInited = true;
      checkReady(onReady);
    });
  };
  document.body.appendChild(gapiScript);

  const gisScript = document.createElement("script");
  gisScript.src = "https://accounts.google.com/gsi/client";
  gisScript.onload = () => {
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: (resp) => {
        if (resp.error) {
          console.error("Token error:", resp.error);
          if (pendingErrorCallback) {
            const cb = pendingErrorCallback;
            pendingCallback = null;
            pendingErrorCallback = null;
            cb(resp.error);
          }
          return;
        }
        saveToken(window.gapi.client.getToken());
        if (pendingCallback) {
          const cb = pendingCallback;
          pendingCallback = null;
          pendingErrorCallback = null;
          cb();
        }
      },
    });
    gisInited = true;
    checkReady(onReady);
  };
  document.body.appendChild(gisScript);
}

// silent: true is for background auto-refresh — it asks Google to renew
// quietly using the existing session (prompt: "") instead of popping the
// full consent dialog. If that quiet renewal fails (session expired,
// third-party cookies blocked, etc.), onError fires and the caller should
// just leave whatever events are already on screen — never force a prompt
// from a background poll the person didn't ask for.
export function signIn(callback, { silent = false, onError } = {}) {
  if (!tokenClient) { console.error("Google not ready yet"); return; }
  const saved = loadToken();
  if (saved) { if (callback) callback(); return; }
  pendingCallback = callback;
  pendingErrorCallback = onError || null;
  tokenClient.requestAccessToken({ prompt: silent ? "" : "consent" });
}

export function signOut() {
  const token = window.gapi.client.getToken();
  if (token) {
    window.google.accounts.oauth2.revoke(token.access_token);
    window.gapi.client.setToken("");
  }
  localStorage.removeItem("gapi-token");
}

// Brenna's calendar is shared with Sean's Google account, so no separate
// sign-in or OAuth grant is needed for her events. Confirm this ID under
// Google Calendar > Settings > "Settings for my calendars" > her calendar >
// "Integrate calendar" > Calendar ID. It's often her email address, but not
// always, if the calendar was shared rather than being her primary one.
const BRENNA_CALENDAR_ID = "brennaprince@gmail.com";

const CALENDAR_SOURCES = [
  { calendarId: "primary", label: "Sean", color: "#4A90D9" },
  { calendarId: BRENNA_CALENDAR_ID, label: "Bre", color: "#E0729C" },
];

const DAY_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function parseEvent(event, label, color) {
  const isAllDay = !event.start.dateTime;

  if (isAllDay) {
    // All-day events: date string is "YYYY-MM-DD", parse without timezone shift
    const parts = event.start.date.split("-");
    const y = parseInt(parts[0]);
    const m = parseInt(parts[1]) - 1;
    const d = parseInt(parts[2]);
    const date = new Date(y, m, d); // local midnight, no timezone shift
    return {
      day: DAY_NAMES[date.getDay()],
      dateKey: event.start.date,
      title: event.summary || "Untitled",
      calendar: label,
      color,
      time: null,
      htmlLink: event.htmlLink, // Google always returns this, even for read-only access — used to open the real event on click
    };
  } else {
    // Timed events: parse the dateTime string
    const dt = event.start.dateTime; // e.g. "2026-07-06T09:00:00-04:00"
    const datePart = dt.substring(0, 10); // "2026-07-06"
    const parts = datePart.split("-");
    const y = parseInt(parts[0]);
    const m = parseInt(parts[1]) - 1;
    const d = parseInt(parts[2]);
    const date = new Date(y, m, d);

    // Parse hour directly from the time portion to avoid timezone issues
    const timePart = dt.substring(11, 16); // "09:00"
    const [hStr, minStr] = timePart.split(":");
    let hour = parseInt(hStr);
    const minute = parseInt(minStr);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    const timeStr = `${displayHour}:${minStr} ${ampm}`;

    // End time, so the calendar can size an event block to its actual
    // duration instead of pinning everything to a single hour cell. Falls
    // back to a 30-minute block if Google somehow didn't send an end time.
    // Doesn't handle an event that crosses midnight (rare for a family
    // calendar) — those just get clamped to a minimum-visible block rather
    // than wrapping into the next day.
    const startMinutes = hour * 60 + minute;
    let endMinutes = startMinutes + 30;
    if (event.end?.dateTime) {
      const endTimePart = event.end.dateTime.substring(11, 16);
      const [ehStr, eminStr] = endTimePart.split(":");
      const parsedEnd = parseInt(ehStr) * 60 + parseInt(eminStr);
      if (parsedEnd > startMinutes) endMinutes = parsedEnd;
    }

    return {
      day: DAY_NAMES[date.getDay()],
      dateKey: datePart, // "2026-07-06"
      title: event.summary || "Untitled",
      calendar: label,
      color,
      time: timeStr,
      hour, // raw 24h hour, kept for anything still reading it directly
      startMinutes,
      endMinutes,
      htmlLink: event.htmlLink, // Google always returns this, even for read-only access — used to open the real event on click
    };
  }
}

async function fetchOneCalendar(calendarId, label, color, timeMin, timeMax) {
  try {
    const response = await window.gapi.client.calendar.events.list({
      calendarId,
      timeMin,
      timeMax,
      showDeleted: false,
      singleEvents: true,
      maxResults: 200,
      orderBy: "startTime",
    });
    return response.result.items.map((event) => parseEvent(event, label, color));
  } catch (err) {
    // Don't let one calendar failing (e.g. sharing revoked, wrong ID) kill the other
    console.error(`Failed to fetch calendar "${label}" (${calendarId}):`, err);
    return [];
  }
}

export async function fetchCalendarEvents() {
  const now = new Date();

  const start = new Date(now);
  start.setDate(now.getDate() - 28);
  start.setHours(0, 0, 0, 0);

  const end = new Date(now);
  end.setDate(now.getDate() + 28);
  end.setHours(23, 59, 59, 999);

  const timeMin = start.toISOString();
  const timeMax = end.toISOString();

  const results = await Promise.all(
    CALENDAR_SOURCES.map((src) =>
      fetchOneCalendar(src.calendarId, src.label, src.color, timeMin, timeMax)
    )
  );

  return results.flat();
}